// Sites — customer locations (doc 03 §3.3 step 2). First-class records that
// outlive the hardware standing on them.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { api, type EndpointSummary, type Me, type Page, type Reseller, type Site } from "../api";
import { Card, ConfirmButton, Empty, ErrorNote, Field, Modal, PageHeader, Spinner, formatDate, useAction } from "../ui";

export default function Sites({ me }: { me: Me }) {
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<Site | "new" | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<Page<Site>>({
    queryKey: ["sites", query],
    queryFn: () => api.get(`/v1/sites?${new URLSearchParams(query ? { q: query } : {})}`),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["sites"] });

  return (
    <>
      <PageHeader
        title="Sites"
        subtitle="Customer locations. The site ID pairs with a service tag as the provisioning key and survives hardware swaps."
        actions={
          <button className="btn-primary" onClick={() => setEditing("new")}>
            New site
          </button>
        }
      />

      <input
        className="input mb-4 max-w-xs"
        placeholder="Search sites…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <Card padded={false}>
        {isLoading ? (
          <Spinner />
        ) : error ? (
          <div className="p-4">
            <ErrorNote error={error} />
          </div>
        ) : !data?.items.length ? (
          <Empty>No sites yet.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Site</th>
                  <th>Customer</th>
                  <th>Code</th>
                  {me.role !== "reseller" && <th>Reseller</th>}
                  <th>Outposts</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.items.map((site) => (
                  <tr key={site.id}>
                    <td className="font-medium">{site.name}</td>
                    <td className="text-ink-300">{site.customer_name ?? "—"}</td>
                    <td className="font-mono text-xs text-ink-400">{site.code ?? "—"}</td>
                    {me.role !== "reseller" && <td className="text-ink-300">{site.reseller_name}</td>}
                    <td className="tabular-nums">{site.endpoint_count ?? 0}</td>
                    <td className="text-ink-400">{formatDate(site.created_at)}</td>
                    <td className="text-right">
                      <button className="btn-ghost" onClick={() => setEditing(site)}>
                        edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <SiteModal
          site={editing === "new" ? null : editing}
          me={me}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

function SiteModal({
  site,
  me,
  onClose,
  onDone,
}: {
  site: Site | null;
  me: Me;
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    name: site?.name ?? "",
    code: site?.code ?? "",
    customer_name: site?.customer_name ?? "",
    address: site?.address ?? "",
    notes: site?.notes ?? "",
    reseller_id: site?.reseller_id ?? me.reseller_id ?? 0,
    contact_name: String(site?.contact_json?.contact_name ?? ""),
    contact_phone: String(site?.contact_json?.phone ?? ""),
    contact_email: String(site?.contact_json?.email ?? ""),
  });
  const { run, busy } = useAction();

  const { data: resellers } = useQuery<Page<Reseller>>({
    queryKey: ["resellers"],
    queryFn: () => api.get("/v1/resellers"),
    enabled: me.role !== "reseller",
  });

  const { data: endpoints } = useQuery<Page<EndpointSummary>>({
    queryKey: ["endpoints", "site", site?.id],
    queryFn: () => api.get(`/v1/endpoints?site_id=${site!.id}`),
    enabled: !!site,
  });

  const save = () =>
    run(async () => {
      const body = {
        name: form.name,
        code: form.code || null,
        customer_name: form.customer_name || null,
        address: form.address || null,
        notes: form.notes || null,
        contact_json: {
          contact_name: form.contact_name || null,
          phone: form.contact_phone || null,
          email: form.contact_email || null,
        },
        ...(site ? {} : { reseller_id: Number(form.reseller_id) }),
      };
      if (site) await api.patch(`/v1/sites/${site.id}`, body);
      else await api.post("/v1/sites", body);
      onDone();
    }, site ? "Site updated" : "Site created");

  return (
    <Modal
      title={site ? `Edit ${site.name}` : "New site"}
      onClose={onClose}
      wide
      footer={
        <>
          {site && (
            <ConfirmButton
              danger
              label="Delete"
              onConfirm={() =>
                run(async () => {
                  await api.del(`/v1/sites/${site.id}`);
                  onDone();
                }, "Site deleted")
              }
            />
          )}
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || !form.name} onClick={save}>
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Site name">
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Site code (optional)" hint="Your own reference, if you use one.">
          <input className="input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
        </Field>
        <Field label="Customer">
          <input
            className="input"
            value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
          />
        </Field>
        {!site && me.role !== "reseller" && (
          <Field label="Reseller">
            <select
              className="input"
              value={form.reseller_id}
              onChange={(e) => setForm({ ...form, reseller_id: Number(e.target.value) })}
            >
              <option value={0}>Select…</option>
              {resellers?.items.map((reseller) => (
                <option key={reseller.id} value={reseller.id}>
                  {reseller.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Address">
          <textarea
            className="input h-20"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
        </Field>
        <div className="space-y-4">
          <Field label="Site contact">
            <input
              className="input"
              placeholder="Name"
              value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <input
              className="input"
              placeholder="Phone"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
            />
            <input
              className="input"
              placeholder="Email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
            />
          </div>
        </div>
        <Field label="Notes">
          <textarea
            className="input h-20"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </Field>
      </div>

      {site && endpoints && endpoints.items.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-400">Outposts at this site</h3>
          <ul className="space-y-1 text-sm">
            {endpoints.items.map((endpoint) => (
              <li key={endpoint.id}>
                <Link to={`/endpoints/${endpoint.id}`} className="service-tag hover:underline">
                  {endpoint.service_tag}
                </Link>
                <span className="ml-2 text-ink-400">
                  {endpoint.state} · {endpoint.channel_count} channels
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}
